import {
  ArrowDown,
  ArrowUp,
  Download,
  Eye,
  FileText,
  Paperclip,
  Search,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { GrInvoicePdfViewer } from "@/components/gr-invoices/GrInvoicePdfViewer";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type GrInvoice, useGrInvoiceList, useIntakeGrInvoice } from "@/hooks/use-gr-invoices";
import { useBillingPeriods } from "@/hooks/use-pay-applications";
import { useProjects } from "@/hooks/use-projects";

type SortKey = keyof Pick<
  GrInvoice,
  "vendorNameRaw" | "invoiceNumber" | "invoiceDate" | "invoiceAmount" | "status" | "intakedAt"
>;

function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "--";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusVariant(
  status: string,
): "default" | "warning" | "success" | "destructive" | "secondary" {
  switch (status) {
    case "pending_pm":
      return "warning";
    case "approved":
      return "success";
    case "rejected":
      return "destructive";
    case "on_hold":
      return "secondary";
    case "posted_to_vista":
    case "included_in_pay_app":
      return "default";
    default:
      return "default";
  }
}

function statusLabel(status: string): string {
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function sortValue(inv: GrInvoice, key: SortKey): string | number {
  if (key === "invoiceAmount") return Number(inv.invoiceAmount) || 0;
  if (key === "intakedAt" || key === "invoiceDate")
    return inv[key] ? new Date(inv[key] as string).getTime() : 0;
  return inv[key] ?? "";
}

function exportCsv(rows: GrInvoice[]) {
  const headers = ["Vendor", "Invoice #", "Invoice Date", "Amount", "Status", "Intaked At"];
  const lines = rows.map((r) =>
    [
      r.vendorNameRaw,
      r.invoiceNumber,
      formatDate(r.invoiceDate),
      r.invoiceAmount,
      r.status,
      formatDate(r.intakedAt),
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(","),
  );
  const csv = [headers.join(","), ...lines].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "gr-invoices.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export function GrIntakePage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("intakedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [sortedColumn, setSortedColumn] = useState<SortKey | null>("intakedAt");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [viewInvoice, setViewInvoice] = useState<GrInvoice | null>(null);

  // Intake form state
  const [file, setFile] = useState<File | null>(null);
  const [projectId, setProjectId] = useState("");
  const [vendorNameRaw, setVendorNameRaw] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [billingPeriodId, setBillingPeriodId] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: projects } = useProjects();
  const { data: billingPeriods } = useBillingPeriods();
  const {
    data: invoices,
    isLoading,
    isError,
    error,
    refetch,
  } = useGrInvoiceList({
    status: statusFilter !== "all" ? (statusFilter as GrInvoice["status"]) : undefined,
    projectId: projectFilter !== "all" ? projectFilter : undefined,
  });

  const intakeMutation = useIntakeGrInvoice();

  const allItems = invoices ?? [];

  const projectOptions = useMemo(() => {
    return (projects ?? [])
      .filter((p) => p.status !== "completed")
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((inv) =>
      [inv.vendorNameRaw, inv.invoiceNumber, formatDate(inv.invoiceDate), inv.status]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [allItems, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (sortedColumn === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortedColumn(key);
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortedColumn !== col) return null;
    return sortDir === "asc" ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    );
  }

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }, []);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    if (picked) setFile(picked);
  }

  function resetForm() {
    setFile(null);
    setProjectId("");
    setVendorNameRaw("");
    setInvoiceNumber("");
    setInvoiceDate("");
    setInvoiceAmount("");
    setBillingPeriodId("");
  }

  function handleSubmit() {
    if (!file || !projectId || !vendorNameRaw || !invoiceNumber || !invoiceDate || !invoiceAmount)
      return;

    const formData = new FormData();
    // orgId is resolved server-side from the session user
    formData.append("file", file);
    formData.append("projectId", projectId);
    formData.append("vendorNameRaw", vendorNameRaw);
    formData.append("invoiceNumber", invoiceNumber);
    formData.append("invoiceDate", invoiceDate);
    formData.append("invoiceAmount", invoiceAmount);
    if (billingPeriodId) formData.append("billingPeriodId", billingPeriodId);

    intakeMutation.mutate(formData, {
      onSuccess: () => {
        setDialogOpen(false);
        resetForm();
      },
    });
  }

  const canSubmit =
    !!file &&
    !!projectId &&
    vendorNameRaw.trim().length > 0 &&
    invoiceNumber.trim().length > 0 &&
    !!invoiceDate &&
    Number(invoiceAmount) > 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load GR invoices"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">GR Invoice Intake</h1>
          <p className="text-sm text-muted-foreground">
            Intake general requirements invoices for PM approval
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Intake Invoice
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search vendor, invoice #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending_pm">Pending PM</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="posted_to_vista">Posted to Vista</SelectItem>
            <SelectItem value="included_in_pay_app">Included in Pay App</SelectItem>
          </SelectContent>
        </Select>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projectOptions.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportCsv(sorted)}
          disabled={sorted.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Download CSV
        </Button>
      </div>

      {/* Table */}
      {sorted.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p>
              {allItems.length === 0
                ? "No GR invoices yet. Use the Intake button to add the first one."
                : "No results match your search."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("vendorNameRaw")}
                >
                  Vendor
                  <SortIcon col="vendorNameRaw" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("invoiceNumber")}
                >
                  Invoice #
                  <SortIcon col="invoiceNumber" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("invoiceDate")}
                >
                  Date
                  <SortIcon col="invoiceDate" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort("invoiceAmount")}
                >
                  Amount
                  <SortIcon col="invoiceAmount" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("status")}
                >
                  Status
                  <SortIcon col="status" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("intakedAt")}
                >
                  Intaked
                  <SortIcon col="intakedAt" />
                </TableHead>
                <TableHead className="w-20">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((inv) => (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setViewInvoice(inv)}
                >
                  <TableCell className="font-medium">{inv.vendorNameRaw}</TableCell>
                  <TableCell>{inv.invoiceNumber}</TableCell>
                  <TableCell>{formatDate(inv.invoiceDate)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(inv.invoiceAmount)}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(inv.status)}>{statusLabel(inv.status)}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(inv.intakedAt)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewInvoice(inv);
                      }}
                    >
                      <Eye className="mr-1 h-3.5 w-3.5" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Intake dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          if (!o) resetForm();
          setDialogOpen(o);
        }}
      >
        <DialogContent className="w-[50vw] max-w-none">
          <DialogHeader>
            <DialogTitle>Intake GR Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Drop zone */}
            <div
              className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
                dragOver
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-muted-foreground/50"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? (
                <div className="flex items-center gap-3">
                  <Paperclip className="h-5 w-5 text-primary" />
                  <span className="font-medium text-sm">{file.name}</span>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="mb-2 h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">
                    Drag & drop PDF or click to browse
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">PDF, JPG, PNG (max 25 MB)</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {/* Form fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  Project <span className="text-destructive">*</span>
                </Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Billing Period</Label>
                <Select value={billingPeriodId} onValueChange={setBillingPeriodId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select period (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {(billingPeriods ?? [])
                      .filter((bp) => bp.status === "OPEN")
                      .map((bp) => (
                        <SelectItem key={bp.id} value={bp.id}>
                          {bp.periodStart} – {bp.periodEnd}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>
                  Vendor Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={vendorNameRaw}
                  onChange={(e) => setVendorNameRaw(e.target.value)}
                  placeholder="e.g. ABC Dumpster Rentals"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Invoice # <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="e.g. INV-2024-001"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Invoice Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>
                  Invoice Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit || intakeMutation.isPending}>
              {intakeMutation.isPending ? "Uploading..." : "Submit Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View invoice dialog */}
      <Dialog open={viewInvoice !== null} onOpenChange={(o) => !o && setViewInvoice(null)}>
        <DialogContent className="flex h-[85vh] w-[85vw] max-w-none flex-col">
          <DialogHeader>
            <DialogTitle>
              {viewInvoice?.vendorNameRaw} — {viewInvoice?.invoiceNumber}
            </DialogTitle>
          </DialogHeader>
          {viewInvoice && (
            <div className="flex-1 overflow-hidden">
              <GrInvoicePdfViewer invoiceId={viewInvoice.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
